import classNames from 'classnames';
import type { CSSProperties } from 'react';
import { useLayoutEffect, useRef, useState } from 'react';

import { Text } from '../../typography/Text';
import styles from './DetailsWithDescription.module.css';

export interface DetailsWithDescriptionProps {
  title?: string;
  detail: string;
  disabled?: boolean;
  maxDetailLines?: 1 | 2;
}

export const DetailsWithDescription = ({
  title,
  detail,
  disabled = false,
  maxDetailLines = 2,
}: DetailsWithDescriptionProps) => {
  const textRef = useRef<HTMLParagraphElement>(null);
  const [detailIsOverflowing, setDetailIsOverflowing] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  useLayoutEffect(() => {
    const checkOverflow = () => {
      const current = textRef.current;
      if (!current) return;
      setDetailIsOverflowing(current.scrollHeight > current.clientHeight);
    };
    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [detail]);

  const handleToggle = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setIsExpanded((v) => !v);
  };
  const clampedStyle: CSSProperties = {
    ['--details-line-clamp' as string]: String(maxDetailLines),
  };

  return (
    <div className={styles.wrapper}>
      {title && (
        <Text fontSize="md" style={{ marginBottom: '0.25rem' }}>
          {title}
        </Text>
      )}
      <Text
        fontSize="x-sm"
        ref={textRef}
        className={classNames(
          styles.description,
          !isExpanded && styles.clamped
        )}
        style={clampedStyle}
        title={detailIsOverflowing ? detail : undefined}
      >
        {detail}
      </Text>
      {(detailIsOverflowing || isExpanded) && (
        <div className={styles.actions}>
          <Text
            as="button"
            fontSize="x-sm"
            type="button"
            className={styles.moreButton}
            onClick={handleToggle}
            disabled={disabled}
          >
            {isExpanded ? 'less' : 'more'}
          </Text>
        </div>
      )}
    </div>
  );
};
